import { INVALID_PRODUCT_TYPES } from '@constants/productConstants'

/**
 * Check if a product is valid for displaying flags
 * @param {Object} product - The product object from Nosto
 * @returns {boolean} - Whether the product is valid for flags
 */
export const isValidProductForFlags = (product) => {
    if (!product) return false

    // Extract product type from Nosto customFields
    const productType = product?.customFields?.find(
        (f) => f.key === 'producttype'
    )?.value

    return !(productType && INVALID_PRODUCT_TYPES.includes(productType))
}

/**
 * Get all product tags (from different possible sources)
 * @param {Array} explicitTags - Tags passed directly to component
 * @param {Object} product - Product object from Nosto
 * @returns {Array} Combined tags array
 */
export const getProductTags = (explicitTags, product) => {
    return explicitTags || product?.tags1 || []
}

/**
 * Process tag object to get relevant offer tags
 * @param {Object} tagObject - Tag mapping object
 * @param {Array} tags - Product tags
 * @returns {Array} Processed offer tags
 */
export const getOfferTags = (tagObject, tags) => {
    if (!tagObject) return []

    return Object.keys(tagObject)
        .map((item) => (tags.includes(item) ? tagObject[item] : null))
        .filter((x) => x !== undefined && x !== null)
}

/**
 * Calculate product price information
 * @param {Object} product - Product from Nosto
 * @returns {Object} Price information
 */
export const calculateProductPricing = (product) => {
    if (!product) return { regularPrice: 0, listPrice: 0 }

    // Try to get price from first variant with inventory
    const firstVariantWithInventory = product.skus?.find(
        (sku) => parseInt(sku.inventoryLevel) > 0
    )

    if (firstVariantWithInventory) {
        return {
            regularPrice: parseFloat(firstVariantWithInventory.price) || 0,
            listPrice: parseFloat(firstVariantWithInventory.listPrice) || 0,
        }
    } else if (product.skus?.length > 0) {
        // Fall back to first SKU
        return {
            regularPrice: parseFloat(product.skus[0].price) || 0,
            listPrice: parseFloat(product.skus[0].listPrice) || 0,
        }
    }

    // Fall back to product level pricing
    return {
        regularPrice: parseFloat(product.price) || 0,
        listPrice: parseFloat(product.listPrice) || 0,
    }
}

/**
 * Determine if a product is on sale and calculate discount
 * @param {Object} product - The product object
 * @param {string} vendor - Product vendor/brand
 * @param {number} regularPrice - Regular price
 * @param {number} listPrice - List price (original/compare at)
 * @returns {Object} Sale status and discount info
 */
export const calculateSaleStatus = (
    product,
    vendor,
    regularPrice,
    listPrice
) => {
    const isDisney =
        vendor === 'Monica + Andy' && (product?.tags1 || []).includes('disney')
    const isSaleProduct = product?.name && product.name.includes('SALE')

    let isOnSale = false
    let discountPercent = 0

    // If there's a list price that's higher than the regular price, product is on sale
    if (listPrice > regularPrice) {
        isOnSale = true

        if (isSaleProduct) {
            // For SALE products, apply additional 40% off the already discounted price
            const additionalDiscountPrice = regularPrice * 0.7

            // Calculate total discount from original list price
            const totalDiscountAmount = listPrice - additionalDiscountPrice
            discountPercent = Math.round(
                (totalDiscountAmount / listPrice) * 100
            )
        } else {
            // For regular discounted products, calculate discount normally
            const discountAmount = listPrice - regularPrice
            discountPercent = Math.round((discountAmount / listPrice) * 100)
        }
    } else if (isSaleProduct && vendor === 'Monica + Andy' && !isDisney) {
        // If product is marked SALE but doesn't have an explicit list price
        isOnSale = true
        discountPercent = 30 // Default 40% discount for Monica + Andy SALE products
    } else if (!isDisney && isOnSale && vendor === 'Monica + Andy') {
        // Regular Monica + Andy products that aren't discounted
        isOnSale = true
    }

    return { isOnSale, discountPercent, isSaleProduct, isDisney }
}
